import { AfterViewInit, Directive, Input, OnDestroy, inject } from '@angular/core';
import { Table } from 'primeng/table';
import { Subscription } from 'rxjs';

/**
 * Remembers the column widths a user drags on a resizable p-table.
 *
 * PrimeNG ships this via `stateKey`, but that persists filters, sort, pagination,
 * selection and expanded rows as well. The filters on these screens are driven by
 * the components' own ngModel bindings, so restoring PrimeNG's copy of that state
 * would leave two sources of truth fighting over the same dropdowns. This stores
 * widths and nothing else.
 *
 * Usage: <p-table [resizableColumns]="true" persistColumnWidths="work-log-report">
 */
@Directive({
  selector: '[persistColumnWidths]',
  standalone: true
})
export class PersistColumnWidthsDirective implements AfterViewInit, OnDestroy {
  /** Storage key for this table. Must be unique across the app. */
  @Input('persistColumnWidths') tableKey = '';

  private readonly table = inject(Table, { optional: true });
  private resizeSub?: Subscription;

  private get storageKey(): string {
    return `apex.colWidths.${this.tableKey}`;
  }

  ngAfterViewInit(): void {
    if (!this.table || !this.tableKey) {
      return;
    }

    this.restore();
    this.resizeSub = this.table.onColResize.subscribe(() => this.save());
  }

  ngOnDestroy(): void {
    this.resizeSub?.unsubscribe();
  }

  /**
   * Reads the widths off the rendered header rather than the drag event, so the
   * stored row always matches the column count actually on screen.
   */
  private save(): void {
    const headers = this.headerCells();
    if (!headers.length) {
      return;
    }

    const widths = headers.map(th => Math.round(th.getBoundingClientRect().width));
    const payload: StoredWidths = { widths };

    // columnResizeMode="expand" grows the table itself, so its width has to come
    // back too or the restored columns would be squeezed into the old table box.
    const tableEl = this.table?.tableViewChild?.nativeElement;
    if (this.table?.columnResizeMode === 'expand' && tableEl) {
      payload.tableWidth = Math.round(tableEl.getBoundingClientRect().width);
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch {
      // Storage full or blocked (private browsing). Resizing still works for the
      // session; it just won't be remembered.
    }
  }

  private restore(): void {
    const stored = this.read();
    if (!stored) {
      return;
    }

    // A column added or removed since the widths were saved invalidates them —
    // applying them by position would shift every column onto the wrong one.
    const headers = this.headerCells();
    if (!headers.length || headers.length !== stored.widths.length) {
      this.clear();
      return;
    }

    const tableEl = this.table?.tableViewChild?.nativeElement;
    if (stored.tableWidth && tableEl) {
      tableEl.style.width = `${stored.tableWidth}px`;
      tableEl.style.minWidth = `${stored.tableWidth}px`;
    }

    stored.widths.forEach((width, index) => {
      this.applyWidth(index, width);
    });
  }

  /**
   * Widths go on the colgroup where the table has one — under table-layout: fixed
   * the <col> wins over the cells, so setting only the cells would be ignored.
   * Tables without a colgroup take it on the header cell instead.
   */
  private applyWidth(index: number, width: number): void {
    const host = this.table?.el?.nativeElement as HTMLElement | undefined;
    const col = host?.querySelectorAll<HTMLTableColElement>('.p-datatable-table > colgroup > col')[index];

    if (col) {
      col.style.width = `${width}px`;
      return;
    }

    const th = this.headerCells()[index];
    if (th) {
      th.style.width = `${width}px`;
      th.style.maxWidth = `${width}px`;
    }
  }

  private headerCells(): HTMLElement[] {
    const host = this.table?.el?.nativeElement as HTMLElement | undefined;
    if (!host) {
      return [];
    }
    return Array.from(host.querySelectorAll<HTMLElement>('.p-datatable-thead > tr:first-child > th'));
  }

  private read(): StoredWidths | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as StoredWidths;
      const valid = Array.isArray(parsed?.widths)
        && parsed.widths.length > 0
        && parsed.widths.every(w => typeof w === 'number' && w > 0);

      return valid ? parsed : null;
    } catch {
      // Unparseable or unreadable — fall back to the template's own widths.
      return null;
    }
  }

  private clear(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // Nothing to do; the stale entry is ignored either way.
    }
  }
}

interface StoredWidths {
  widths: number[];
  tableWidth?: number;
}
